import Grid from "components/primitives/Grid";
import Typography from "@mui/material/Typography";
import Link from "components/Link";
import { useCallback, useState, useMemo } from "react";
import CloudUpIcon from "components/Icons/CloudUpIcon";
import * as Components from "./FileUploadArea.components";
import { Trans, useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import GroupedOptionButtons from "components/GroupedOptionButtons";
import Div from "components/primitives/Div";

interface FileUploadAreaProps {
  onFileSelect: (file: File) => void;
  disabled?: boolean;
  imperialSystemEnabled: boolean;
  onToggleMeasurementSystem: () => void;
}

function FileUploadArea(props: FileUploadAreaProps) {
  const { onFileSelect, disabled = false, imperialSystemEnabled, onToggleMeasurementSystem } = props;
  const [dragHovered, setDragHovered] = useState(false);
  const [error, setError] = useState<boolean>(false);
  const { t } = useTranslation();

  const alertMessage = useMemo(
    () =>
      imperialSystemEnabled
        ? t("athlete.bulk_1rm_measurement_unit_warning_imperial")
        : t("athlete.bulk_1rm_measurement_unit_warning_metric"),
    [imperialSystemEnabled, t]
  );

  const handleFileChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    async ({ target }) => {
      setDragHovered(false);
      setError(false);

      try {
        const file = target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith(".csv")) {
          setError(true);
          return;
        }

        onFileSelect(file);
      } catch (err) {
        setError(true);
      }
    },
    [onFileSelect]
  );

  const handleDragEnter = useCallback(() => {
    if (!disabled) {
      setDragHovered(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback(() => setDragHovered(false), []);

  const handleDrop = useCallback(() => {}, []);

  const options = useMemo(
    () => [
      {
        label: `${t("settings.select_metric_system_label")} (KG)`,
        "aria-label": t("settings.select_metric_system_aria_label"),
        value: false,
      },
      {
        label: `${t("settings.select_imperial_system_label")} (LBS)`,
        "aria-label": t("settings.select_imperial_system_aria_label"),
        value: true,
      },
    ],
    [t]
  );

  return (
    <Div sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Alert severity="warning">
        <AlertTitle>{t("athlete.bulk_1rm_measurement_unit_warning_title")}</AlertTitle>
        <Typography variant="body2">{alertMessage}</Typography>
      </Alert>
      <Div sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 300 }}>
          {t("settings.measurement_system_measure_all_units")}
        </Typography>
        <GroupedOptionButtons
          value={imperialSystemEnabled}
          onClick={onToggleMeasurementSystem}
          ButtonProps={{ size: "large" }}
          options={options}
        />
        <Typography variant="caption" sx={{ fontWeight: 300, color: "text.secondary", mt: 0.5 }}>
          {t("athlete.bulk_1rm_measurement_system_account_warning")}
        </Typography>
      </Div>
      <Components.Root>
        <Components.InputLabel dragHovered={dragHovered} error={error}>
          <Components.Input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            disabled={disabled}
          />
          <Grid
            container
            sx={{
              width: "100%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Grid sx={{ minWidth: 240, maxWidth: 320, textAlign: "center" }}>
              <CloudUpIcon color="secondary" sx={{ fontSize: 48 }} aria-label={t("global.upload_here")} />
              <Typography variant="h4" align="center" gutterBottom sx={{ color: "grey.800" }}>
                {error && <>{t("athlete.bulk_1rm_invalid_file_error")} </>}
                <Trans
                  t={t}
                  i18nKey={!error ? "athlete.bulk_1rm_upload_prompt" : "athlete.bulk_1rm_upload_prompt_retry"}
                >
                  Drag your CSV here or
                  <Link component="span" color="primary">
                    browse
                  </Link>
                </Trans>
              </Typography>
            </Grid>
          </Grid>
        </Components.InputLabel>
      </Components.Root>
    </Div>
  );
}

export default FileUploadArea;
